/**
 * Home Page
 *
 * The main search page of the application.
 */

import { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Pagination from '@mui/material/Pagination';
import Typography from '@mui/material/Typography';
import { SearchBar, SearchFilterChips, SearchResults, StatusMessage, FeedbackWidget } from '../components';
import { APP_BAR_SEARCH_SLOT_ID } from '../components/TopAppBar';
import { useSearchController } from '../hooks/useSearchController';
import type { SearchFilters } from '../types/search';

export function HomePage() {
  const { t } = useTranslation('search');
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    query,
    setQuery,
    filters,
    setFilters,
    filtersRef,
    results,
    isLoading,
    error,
    hasSearched,
    page,
    totalPages,
    search,
    applyFilters,
    goToPage,
    reset,
  } = useSearchController();

  // Track whether the initial ?q= search has been executed
  const initialSearchDone = useRef(false);

  // Reset state when navigating back to home via logo click
  useEffect(() => {
    if (location.state?.resetKey) {
      reset();
      initialSearchDone.current = false;
      setSearchParams({}, { replace: true });
    }
  }, [location.state?.resetKey, reset, setSearchParams]);

  // Auto-search from URL parameters (used by external widget integration)
  // Supports ?q=<query>&source=<v>&source_type=<v>&zenon_version=<v>&language=<v>
  useEffect(() => {
    const q = searchParams.get('q');
    if (q && !initialSearchDone.current) {
      initialSearchDone.current = true;

      // Read optional filter params (repeated params become arrays)
      const FILTER_PARAMS = ['source', 'source_type', 'zenon_version', 'language'] as const;
      const urlFilters: SearchFilters = {};
      for (const param of FILTER_PARAMS) {
        const values = searchParams.getAll(param);
        if (values.length > 0) {
          urlFilters[param] = values;
        }
      }

      setQuery(q);
      if (Object.keys(urlFilters).length > 0) {
        setFilters(urlFilters);
        filtersRef.current = urlFilters;
      }
      search(q);
    }
  }, [searchParams, search, setQuery, setFilters, filtersRef]);

  // Keep URL in sync with current query
  useEffect(() => {
    if (hasSearched && query.trim()) {
      setSearchParams({ q: query }, { replace: true });
    } else if (!query.trim()) {
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setSearchParams is stable; avoid loop from searchParams dep
  }, [hasSearched, query]);

  /** Called when pagination changes. */
  const handlePageChange = useCallback(
    (_event: React.ChangeEvent<unknown>, newPage: number) => {
      goToPage(newPage);
      // Scroll to top of results
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [goToPage]
  );

  // Determine if we should show the centered layout (no results yet)
  const showCenteredLayout = !hasSearched && !results;

  return (
    <Box sx={{ py: 4 }}>
      {/* Centered Layout - Before Search */}
      {showCenteredLayout && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '50vh',
            px: { xs: 2, sm: 3 },
            color: 'primary.main',
          }}
        >
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            textAlign="center"
            fontWeight={400}
          >
            {t('headline')}
          </Typography>
          <Box sx={{ height: 16 }} />

          <Box sx={{ width: '100%', maxWidth: 800 }}>
            <SearchBar
              query={query}
              onQueryChange={setQuery}
              onSearch={search}
              isLoading={isLoading}
              showAllFiltersButton
              filters={filters}
              onFiltersChange={setFilters}
            />
          </Box>
        </Box>
      )}

      {/* Results Layout - After Search */}
      {!showCenteredLayout && (
        <Box sx={{ maxWidth: 1100, pl: { xs: 2, sm: '224px' }, pr: 3 }}>
          {/* SearchBar portalled into the AppBar */}
          {(() => {
            const slot = document.getElementById(APP_BAR_SEARCH_SLOT_ID);
            if (!slot) return null;
            return createPortal(
              <SearchBar
                query={query}
                onQueryChange={setQuery}
                onSearch={search}
                isLoading={isLoading}
              />,
              slot
            );
          })()}

          {/* Filter Chips */}
          <Box sx={{ mb: 3 }}>
            <SearchFilterChips
              filters={filters}
              onChange={applyFilters}
            />
          </Box>

          {/* Error Message */}
          {error && (
            <StatusMessage event="server-error" />
          )}

          {/* Search Results */}
          {!error && (
            <SearchResults
              results={results}
              isLoading={isLoading}
              query={query}
            />
          )}

          {/* Pagination */}
          {!error && results && totalPages > 1 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 4,
                mb: 2,
              }}
            >
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                shape="rounded"
                showFirstButton
                showLastButton
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: 2,
                    fontWeight: 500,
                  },
                }}
              />
            </Box>
          )}
        </Box>
      )}

      {/* Floating feedback button — only on the result view, per the concept */}
      {!showCenteredLayout && <FeedbackWidget searchQuery={query} />}
    </Box>
  );
}
